import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "@/lib/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = ({ kind = "contact" }) => {
  const { t } = useTranslation();
  const lang = useLang();
  const [form, setForm] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const payload = {
        name: form.name || "",
        email: form.email || "",
        organization: form.organization || null,
        message: form.message || "",
        website: form.website || "",
        privacyAccepted: form.consent === true,
        lang,
      };
      if (kind === "demo") {
        payload.establishment = form.establishment || "";
        payload.rooms = form.rooms ? Number(form.rooms) : null;
        payload.phone = form.phone || null;
      }
      if (kind === "partenaire") {
        payload.organization = form.organization || "";
        payload.partnership_type = form.partnership_type || "";
      }
      await axios.post(`${API}/${kind}`, payload);
      setStatus("done");
      toast.success(t("form.success_title"));
    } catch (err) {
      setStatus("idle");
      toast.error(t("form.error"));
    }
  };

  const inputCls =
    "w-full rounded-lg border border-ostelo-deep/15 bg-white px-4 py-3 text-sm text-ostelo-ink placeholder:text-ostelo-ink/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-ostelo-blue focus:ring-2 focus:ring-ostelo-blue/15";
  const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-ostelo-deep/70";

  if (status === "done") {
    return (
      <div data-testid={`${kind}-form-success`} className="flex flex-col items-start gap-4 rounded-2xl border border-ostelo-green/30 bg-ostelo-mint p-8">
        <CheckCircle2 size={32} className="text-ostelo-green" />
        <p className="font-display text-2xl font-semibold text-ostelo-deep">{t("form.success_title")}</p>
        <p className="text-sm leading-relaxed text-ostelo-ink/70">{t("form.success")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid={`${kind}-form`} className="space-y-5">
      <input
        type="text"
        name="website"
        value={form.website || ""}
        onChange={set("website")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute -left-[9999px] h-0 w-0 opacity-0"
      />
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor={`${kind}-name`} className={labelCls}>{t("form.name")}</label>
          <input id={`${kind}-name`} data-testid={`${kind}-input-name`} required minLength={2} placeholder={t("form.name_ph")} className={inputCls} onChange={set("name")} />
        </div>
        <div>
          <label htmlFor={`${kind}-email`} className={labelCls}>{t("form.email")}</label>
          <input id={`${kind}-email`} data-testid={`${kind}-input-email`} type="email" required placeholder={t("form.email_ph")} className={inputCls} onChange={set("email")} />
        </div>
      </div>

      {kind === "demo" && (
        <div className="grid gap-5 sm:grid-cols-3">
          <div className="sm:col-span-1">
            <label htmlFor="demo-establishment" className={labelCls}>{t("form.establishment")}</label>
            <input id="demo-establishment" data-testid="demo-input-establishment" required minLength={2} placeholder={t("form.est_ph")} className={inputCls} onChange={set("establishment")} />
          </div>
          <div>
            <label htmlFor="demo-rooms" className={labelCls}>{t("form.rooms")}</label>
            <input id="demo-rooms" data-testid="demo-input-rooms" type="number" min="1" max="5000" placeholder={t("form.rooms_ph")} className={inputCls} onChange={set("rooms")} />
          </div>
          <div>
            <label htmlFor="demo-phone" className={labelCls}>{t("form.phone")}</label>
            <input id="demo-phone" data-testid="demo-input-phone" type="tel" className={inputCls} onChange={set("phone")} />
          </div>
        </div>
      )}

      {kind === "partenaire" ? (
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="partner-org" className={labelCls}>{t("form.organization")}</label>
            <input id="partner-org" data-testid="partenaire-input-organization" required minLength={2} placeholder={t("form.org_ph")} className={inputCls} onChange={set("organization")} />
          </div>
          <div>
            <label htmlFor="partner-type" className={labelCls}>{t("form.ptype")}</label>
            <select id="partner-type" data-testid="partenaire-select-type" required defaultValue="" className={inputCls} onChange={set("partnership_type")}>
              <option value="" disabled>—</option>
              <option value="territoire">{t("form.ptype_territory")}</option>
              <option value="institution">{t("form.ptype_institution")}</option>
              <option value="entreprise">{t("form.ptype_business")}</option>
              <option value="autre">{t("form.ptype_other")}</option>
            </select>
          </div>
        </div>
      ) : (
        kind === "contact" && (
          <div>
            <label htmlFor="contact-org" className={labelCls}>{t("form.organization")}</label>
            <input id="contact-org" data-testid="contact-input-organization" placeholder={t("form.org_ph")} className={inputCls} onChange={set("organization")} />
          </div>
        )
      )}

      <div>
        <label htmlFor={`${kind}-message`} className={labelCls}>{t("form.message")}</label>
        <textarea
          id={`${kind}-message`}
          data-testid={`${kind}-input-message`}
          required
          minLength={5}
          rows={5}
          placeholder={kind === "demo" ? t("form.msg_demo_ph") : t("form.msg_ph")}
          className={`${inputCls} resize-none`}
          onChange={set("message")}
        />
      </div>

      <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-ostelo-ink/60">
        <input type="checkbox" required data-testid={`${kind}-consent-checkbox`} className="mt-0.5 h-4 w-4 rounded border-ostelo-deep/30 accent-[#247BA0]" onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} />
        {t("form.consent")}
      </label>

      <button
        type="submit"
        disabled={status === "sending"}
        data-testid={`${kind}-submit-button`}
        className="inline-flex items-center gap-2 rounded-lg bg-ostelo-deep px-7 py-3.5 text-sm font-semibold text-white shadow-[0_8px_30px_rgb(23,63,95,0.18)] transition-[background-color,transform] duration-300 hover:scale-[0.98] hover:bg-ostelo-blue disabled:opacity-60"
      >
        {status === "sending" && <Loader2 size={16} className="animate-spin" />}
        {status === "sending" ? t("form.sending") : kind === "demo" ? t("form.submit_demo") : t("form.submit")}
      </button>
    </form>
  );
};
